import type {
  RawCanvasData,
  VariableValue,
  WorkflowVariable,
  WorkflowExecutionStatus,
} from '@refly/openapi-schema';
import { useTranslation } from 'react-i18next';
import { Button, Input, Form, Typography, message, Tooltip, Modal } from 'antd';
import { StopCircle } from 'refly-icons';
import { useState, useEffect, useCallback, useMemo } from 'react';
import type { UploadFile } from 'antd/es/upload/interface';
import { LuUsers, LuX } from 'react-icons/lu';
import { useAbortWorkflow } from '@refly-packages/ai-workspace-common/hooks/use-abort-workflow';
import cn from 'classnames';
import EmptyImage from '@refly-packages/ai-workspace-common/assets/noResource.webp';
import { useIsLogin } from '@refly-packages/ai-workspace-common/hooks/use-is-login';
import { useNavigate } from 'react-router-dom';
import { ToolsDependencyChecker } from '@refly-packages/ai-workspace-common/components/canvas/tools-dependency';
import {
  AccountTypeForm,
  ResourceTypeForm,
  type Account,
  type MediaFile,
} from '@refly-packages/ai-workspace-common/components/canvas/workflow-variables';
import { getFileType } from '@refly-packages/ai-workspace-common/components/canvas/workflow-variables/utils';
import { useSubscriptionStoreShallow } from '@refly/stores';
import { useCanvasResourcesPanelStoreShallow } from '@refly/stores';
import { useSubscriptionUsage } from '@refly-packages/ai-workspace-common/hooks/use-subscription-usage';
import {
  useGetCanvasData,
  useListUserTools,
} from '@refly-packages/ai-workspace-common/queries/queries';
import type { GenericToolset, UserTool } from '@refly/openapi-schema';
import { extractToolsetsWithNodes, ToolWithNodes } from '@refly/canvas-common';
import GiftIcon from '@refly-packages/ai-workspace-common/assets/gift.png';
import { useFirstSuccessExecutionToday } from '@refly-packages/ai-workspace-common/hooks/canvas';
import { useUserMembership } from '@refly-packages/ai-workspace-common/hooks/use-user-membership';

const PLATFORM_MAP: Record<number, string> = {
  1: '小红书',
  2: '视频号',
  3: '抖音',
  4: '快手',
  5: 'TikTok',
  6: 'Instagram',
  7: 'Facebook',
};

const normalizeOptionValue = (value: any) => {
  if (value?.account?.accountId) return value;
  return {
    type: 'text' as const,
    text: typeof value === 'string' ? value : (value?.text ?? ''),
  };
};

/**
 * Check if a toolset is authorized/installed.
 * - MCP servers: installed if the server exists in userTools.
 * - Builtin tools: always available.
 * - OAuth tools: installed only when authorized.
 */
const isToolsetAuthorized = (toolset: GenericToolset, userTools: UserTool[]): boolean => {
  if (toolset.type === 'mcp') {
    return userTools.some((t) => t.toolset?.name === toolset.name);
  }

  if (toolset.builtin) {
    return true;
  }

  const matchingUserTool = userTools.find((t) => t.key === toolset.toolset?.key);
  if (!matchingUserTool) {
    return false;
  }

  return matchingUserTool.authorized ?? false;
};

const RequiredTagText = () => {
  return null;
};

const EmptyContent = () => {
  const { t } = useTranslation();
  return (
    <div className="w-full h-full flex flex-col items-center justify-center">
      <img src={EmptyImage} alt="no variables" className="w-[120px] h-[120px] -mb-4" />
      <div className="text-sm text-refly-text-2 leading-5">
        {t('canvas.workflow.run.emptyTitle', 'No variables defined')}
      </div>
      <div className="text-sm text-refly-text-2 leading-5">
        {t(
          'canvas.workflow.run.emptyDescription',
          ' the workflow will be executed once if continued.',
        )}
      </div>
    </div>
  );
};

const FormItemLabel = ({ name, required }: { name: string; required: boolean }) => {
  return (
    <div className="flex items-center gap-2 min-w-0">
      <Typography.Paragraph
        ellipsis={{ rows: 1, tooltip: <div className="max-h-[200px] overflow-y-auto">{name}</div> }}
        className="!m-0 text-xs font-semibold text-refly-text-0 leading-4 max-w-[100px]"
      >
        {name}
      </Typography.Paragraph>

      {required && <RequiredTagText />}
    </div>
  );
};

interface WorkflowRunFormProps {
  workflowVariables: WorkflowVariable[];
  onSubmitVariables: (variables: WorkflowVariable[]) => Promise<void>;
  loading: boolean;
  executionId?: string | null;
  workflowStatus?: WorkflowExecutionStatus | null;
  isPolling?: boolean;
  pollingError?: any;
  isRunning?: boolean;
  onRunningChange?: (isRunning: boolean) => void;
  canvasId?: string;
  className?: string;
  templateContent?: string;
  workflowApp?: any;
  creditUsage?: number | null;
}

export const WorkflowRunForm = ({
  workflowVariables,
  onSubmitVariables,
  loading,
  executionId,
  isPolling,
  isRunning: externalIsRunning,
  onRunningChange,
  canvasId,
  className,
  templateContent,
  workflowApp,
  creditUsage,
}: WorkflowRunFormProps) => {
  const { t } = useTranslation();
  const { isLoggedRef, userProfile } = useIsLogin();
  const isLogin = !!userProfile?.uid;
  const navigate = useNavigate();
  const { setCreditInsufficientModalVisible } = useSubscriptionStoreShallow((state) => ({
    setCreditInsufficientModalVisible: state.setCreditInsufficientModalVisible,
  }));
  const { creditBalance, isBalanceSuccess } = useSubscriptionUsage();

  useFirstSuccessExecutionToday();

  const { setToolsDependencyOpen, setToolsDependencyHighlight, hasFirstSuccessExecutionToday } =
    useCanvasResourcesPanelStoreShallow((state) => ({
      setToolsDependencyOpen: state.setToolsDependencyOpen,
      setToolsDependencyHighlight: state.setToolsDependencyHighlight,
      hasFirstSuccessExecutionToday: state.hasFirstExecutionToday,
    }));

  const { planType } = useUserMembership();

  const [internalIsRunning, setInternalIsRunning] = useState(false);
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);

  const [fallbackToolsCanvasData, setFallbackToolsCanvasData] = useState<RawCanvasData | undefined>(
    undefined,
  );

  // Use external isRunning if provided, otherwise use internal state
  const isRunning = externalIsRunning ?? internalIsRunning;

  const { data: userToolsData } = useListUserTools({}, [], {
    enabled: isLogin,
    refetchOnWindowFocus: false,
  });
  const userTools = userToolsData?.data ?? [];

  const { data: canvasResponse, refetch: refetchCanvasData } = useGetCanvasData(
    { query: { canvasId: canvasId ?? '' } },
    [],
    {
      enabled: !!canvasId && isLogin,
      refetchOnWindowFocus: false,
    },
  );

  const nodesFromWorkflowApp = workflowApp?.canvasData?.nodes || [];
  const nodesFromCanvas = canvasResponse?.data?.nodes || [];
  const nodes = nodesFromWorkflowApp.length > 0 ? nodesFromWorkflowApp : nodesFromCanvas;

  const toolsDependencyCanvasData: RawCanvasData | undefined =
    workflowApp?.canvasData ?? canvasResponse?.data ?? fallbackToolsCanvasData;

  // Abort workflow with optimistic UI update (immediately marks nodes as 'failed')
  const { handleAbort } = useAbortWorkflow({
    executionId,
    canvasId,
    onSuccess: () => {
      // Reset running state after successful abort
      if (onRunningChange) {
        onRunningChange(false);
      } else {
        setInternalIsRunning(false);
      }
    },
  });
  const [form] = Form.useForm();
  const [variableValues, setVariableValues] = useState<Record<string, any>>({});
  const [templateVariables, setTemplateVariables] = useState<WorkflowVariable[]>([]);
  const [selectionVariable, setSelectionVariable] = useState<WorkflowVariable | null>(null);

  // Check if form should be disabled
  const isFormDisabled = loading || isRunning || isPolling;

  // Check if all required fields are filled
  const isFormValid = useMemo(() => {
    return workflowVariables.every((variable) => {
      if (!variable.required) {
        return true;
      }

      const value = variableValues[variable.name];

      if (variable.variableType === 'string') {
        return value && value.trim() !== '';
      }

      if (variable.variableType === 'option') {
        return value && (Array.isArray(value) ? value.length > 0 : value);
      }

      if (variable.variableType === 'resource') {
        return value && Array.isArray(value) && value.length > 0;
      }

      return false;
    });
  }, [workflowVariables, variableValues]);

  // Get list of invalid required fields
  const getInvalidFields = useCallback(() => {
    const invalidFields: string[] = [];
    for (const variable of workflowVariables) {
      if (!variable.required) continue;

      const value = variableValues[variable.name];
      let isValid = false;

      if (variable.variableType === 'string') {
        isValid = value && value.trim() !== '';
      } else if (variable.variableType === 'option') {
        isValid = value && (Array.isArray(value) ? value.length > 0 : !!value);
      } else if (variable.variableType === 'resource') {
        isValid = value && Array.isArray(value) && value.length > 0;
      }

      if (!isValid) {
        invalidFields.push(variable.name);
      }
    }
    return invalidFields;
  }, [workflowVariables, variableValues]);

  // Check if a specific field is invalid (for showing error state)
  const isFieldInvalid = useCallback(
    (variableName: string) => {
      return attemptedSubmit && getInvalidFields().includes(variableName);
    },
    [attemptedSubmit, getInvalidFields],
  );

  const convertVariableToFormValue = useCallback(() => {
    const formValues: Record<string, any> = {};

    for (const variable of workflowVariables) {
      if (variable.variableType === 'string') {
        formValues[variable.name] = variable.value?.[0]?.text ?? '';
      } else if (variable.variableType === 'option') {
        // Account variables are read-only in the run form; keep the stored
        // account values as the form value so validation can rely on length.
        formValues[variable.name] = Array.isArray(variable.value) ? variable.value : [];
      } else if (variable.variableType === 'resource') {
        // Convert resource values to UploadFile format
        const fileList: UploadFile[] =
          variable.value?.map((v, index) => {
            const fileId = v.resource?.fileId;
            const entityId = v.resource?.entityId;
            const assetId = v.resource?.assetId;

            return {
              uid: assetId || fileId || entityId || `file-${index}`,
              name: v.resource?.name || '',
              status: 'done' as const,
              url: v.resource?.storageKey || '',
              type: v.resource?.fileType,
              // Store fileId / assetId in response for later retrieval
              ...(assetId ? { response: { assetId } } : fileId && { response: { fileId } }),
            };
          }) || [];
        formValues[variable.name] = fileList;
      }
    }

    return formValues;
  }, [workflowVariables]);

  const convertFormValueToVariable = useCallback(() => {
    const newVariables: WorkflowVariable[] = [];
    for (const variable of workflowVariables) {
      const value = variableValues[variable.name];
      if (variable.variableType === 'string') {
        newVariables.push({
          ...variable,
          value: [{ type: 'text', text: value }],
        });
      } else if (variable.variableType === 'option') {
        const valueArray = Array.isArray(value) ? value : value ? [value] : [];
        newVariables.push({
          ...variable,
          // Account pickers already provide WorkflowVariable values containing
          // account metadata. Re-wrapping them as `text` values turns the
          // object into an invalid account ID at execution time.
          value: valueArray.map(normalizeOptionValue),
        });
      } else if (variable.variableType === 'resource') {
        const files = Array.isArray(value) ? value : [];
        // 素材库条目可以多选，按 assetId 原样保留；上传文件仍沿用原单文件逻辑。
        const libraryValues: VariableValue[] = files
          .filter((file) => file?.response?.assetId)
          .map((file) => ({
            type: 'resource',
            resource: {
              name: file.name,
              fileType: getFileType(file.name, file.type),
              assetId: file.response.assetId,
              source: 'media_library',
            },
          }));
        if (libraryValues.length) {
          newVariables.push({ ...variable, value: libraryValues });
          continue;
        }
        const v = files[0];
        const entityId = variable?.value?.[0]?.resource?.entityId;
        const existingFileId = variable?.value?.[0]?.resource?.fileId;

        if (v) {
          // Extract fileId from upload response if available
          const uploadedFileId = v.response?.fileId || v.uid;
          // Use uploaded fileId if it looks like a fileId (starts with 'df-'),
          // otherwise use existing fileId from variable
          const fileId = uploadedFileId?.startsWith?.('df-') ? uploadedFileId : existingFileId;

          newVariables.push({
            ...variable,
            value: [
              {
                type: 'resource',
                resource: {
                  name: v.name,
                  storageKey: v.url,
                  fileType: getFileType(v.name, v.type),
                  ...(fileId && { fileId }),
                  ...(entityId && { entityId }),
                },
              },
            ],
          });
        } else {
          // Keep the variable even if no file is uploaded (with empty value)
          newVariables.push({
            ...variable,
            value: [],
          });
        }
      }
    }
    return newVariables;
  }, [workflowVariables, variableValues]);

  // Handle form value changes
  const handleValueChange = (variableName: string, value: any) => {
    setVariableValues((prev) => ({
      ...prev,
      [variableName]: value,
    }));
  };

  const handleLibraryFilesSelect = useCallback(
    (files: MediaFile[]) => {
      if (!selectionVariable) return;
      const selectedFiles: UploadFile[] = files.map((file) => ({
        uid: file.fileId,
        name: file.name,
        status: 'done',
        url: file.storageKey || file.url || '',
        type: file.type,
        response: file.assetId ? { assetId: file.assetId } : { fileId: file.fileId },
      }));
      handleValueChange(selectionVariable.name, selectedFiles);
      form.setFieldsValue({ [selectionVariable.name]: selectedFiles });
    },
    [selectionVariable, form],
  );

  const handleAccountsSelect = useCallback(
    (accounts: Account[]) => {
      if (!selectionVariable) return;
      const values = accounts.map((account) => ({
        type: 'text',
        text: account.accountId,
        account: {
          accountId: account.accountId,
          userName: account.userName,
          platform: account.platform,
          platformType: account.platformType,
          filePath: account.filePath,
          categoryName: account.categoryName,
        },
      }));
      handleValueChange(selectionVariable.name, values);
      form.setFieldsValue({ [selectionVariable.name]: values });
    },
    [selectionVariable, form],
  );

  const handleAccountRemove = useCallback(
    (variable: WorkflowVariable, accountId: string) => {
      const currentValues = Array.isArray(variableValues[variable.name])
        ? variableValues[variable.name]
        : variable.value || [];
      const nextValues = currentValues.filter(
        (value: any) => value?.account?.accountId !== accountId,
      );
      handleValueChange(variable.name, nextValues);
      form.setFieldsValue({ [variable.name]: nextValues });
    },
    [form, variableValues],
  );

  // Initialize template variables when templateContent changes
  useEffect(() => {
    if (templateContent) {
      // Extract variables from template content
      const variableRegex = /\{\{([^}]+)\}\}/g;
      const templateVariableNames = new Set<string>();
      let match: RegExpExecArray | null;

      match = variableRegex.exec(templateContent);
      while (match !== null) {
        templateVariableNames.add(match[1].trim());
        match = variableRegex.exec(templateContent);
      }

      // Filter workflowVariables to only include those mentioned in template
      const relevantVariables = workflowVariables.filter((variable) =>
        templateVariableNames.has(variable.name),
      );

      setTemplateVariables(relevantVariables);
    }
  }, [templateContent, workflowVariables]);

  // Update form values when workflowVariables change
  useEffect(() => {
    const newValues = convertVariableToFormValue();
    setVariableValues(newValues);
    form.setFieldsValue(newValues);
  }, [workflowVariables, convertVariableToFormValue]);

  const handleRun = async () => {
    // Mark that user has attempted to submit (for showing validation errors)
    setAttemptedSubmit(true);

    // Check if form is valid - if not, return early (errors will be shown due to attemptedSubmit)
    if (!isFormValid) {
      return;
    }

    if (loading || isRunning) {
      return;
    }

    // Check if user is logged in
    if (!isLoggedRef.current) {
      // Redirect to login with return URL
      const returnUrl = encodeURIComponent(window.location.pathname + window.location.search);
      navigate(`/?autoLogin=true&returnUrl=${returnUrl}`);
      return;
    }

    // Ensure we have canvas nodes to calculate tool dependencies.
    let effectiveNodes = nodes;
    if (!effectiveNodes.length && canvasId && isLogin) {
      try {
        const result = await refetchCanvasData();
        effectiveNodes = (result as any)?.data?.data?.nodes ?? [];
      } catch {
        effectiveNodes = [];
      }
    }

    const effectiveUninstalledCount = (() => {
      if (!effectiveNodes.length) return 0;
      const toolsetsWithNodes = extractToolsetsWithNodes(effectiveNodes);
      return toolsetsWithNodes.filter((tool: ToolWithNodes) => {
        return !isToolsetAuthorized(tool.toolset, userTools);
      }).length;
    })();

    // Check if there are uninstalled tools
    if (effectiveUninstalledCount > 0) {
      if (!toolsDependencyCanvasData) {
        setFallbackToolsCanvasData({
          nodes: effectiveNodes,
          edges: [],
        });
      }

      message.warning(t('canvas.workflow.run.installToolsBeforeRunning'));
      if (canvasId) {
        setToolsDependencyOpen(canvasId, true);
        setToolsDependencyHighlight(canvasId, true);
      }
      return;
    }

    // Frontend pre-check: if current credit balance is insufficient, open the modal and stop.
    // This is best-effort and only runs when balance data is available, to avoid false negatives.
    const requiredCredits = Number(creditUsage ?? 0);
    const isRequiredCreditsValid = Number.isFinite(requiredCredits) && requiredCredits > 0;
    if (isBalanceSuccess && isRequiredCreditsValid && creditBalance < requiredCredits) {
      setCreditInsufficientModalVisible(true, undefined, 'canvas');
      return;
    }

    try {
      let newVariables: WorkflowVariable[] = [];

      if (templateContent) {
        // Validate templateVariables values
        const hasInvalidValues = templateVariables.some((variable) => {
          if (!variable.value || variable.value.length === 0) {
            return true;
          }

          return variable.value.every((val) => {
            if (variable.variableType === 'string') {
              return !val.text || val.text.trim() === '';
            }
            if (variable.variableType === 'option') {
              return !val.account?.accountId && (!val.text || val.text.trim() === '');
            }
            if (variable.variableType === 'resource') {
              return !val.resource || !val.resource.storageKey;
            }
            return true;
          });
        });

        if (hasInvalidValues) {
          // Show validation error message
          message.warning(
            t(
              'canvas.workflow.run.validationError',
              'Please fill in all required fields before running the workflow',
            ),
          );
          return;
        }

        // The account picker updates form state. Submit the normalized current
        // values here as well, otherwise template mode drops manual selections.
        newVariables = convertFormValueToVariable();
      } else {
        // Validate form before running
        await form.validateFields();

        // If validation passes, proceed with running
        newVariables = convertFormValueToVariable();
      }

      // Set running state - use external callback if provided, otherwise use internal state
      if (onRunningChange) {
        onRunningChange(true);
      } else {
        setInternalIsRunning(true);
      }

      await onSubmitVariables(newVariables);

      // Reset running state on validation error
      if (onRunningChange) {
        onRunningChange(false);
      } else {
        setInternalIsRunning(false);
      }
    } catch (error) {
      // Form validation failed, scroll to first error
      if (error?.errorFields && error.errorFields.length > 0) {
        const firstErrorField = error.errorFields[0];
        const fieldName = firstErrorField.name;
        if (Array.isArray(fieldName) && fieldName.length > 0) {
          const fieldNameStr = fieldName[0];

          // Try to find the form item container first
          let element = document.querySelector(`[name="${fieldNameStr}"]`) as HTMLElement;

          if (!element) {
            // If direct input not found, try to find the form item container
            element = document.querySelector(`[data-field-name="${fieldNameStr}"]`) as HTMLElement;
          }

          if (element) {
            // Find the scrollable container
            const scrollContainer = document.querySelector(
              '.p-4.flex-1.overflow-y-auto',
            ) as HTMLElement;

            if (scrollContainer) {
              // Calculate the scroll position
              const containerRect = scrollContainer.getBoundingClientRect();
              const elementRect = element.getBoundingClientRect();
              const currentScrollTop = scrollContainer.scrollTop;
              const targetScrollTop = currentScrollTop + elementRect.top - containerRect.top - 40;

              // Smooth scroll to the target position
              scrollContainer.scrollTo({
                top: targetScrollTop,
                behavior: 'smooth',
              });
            } else {
              // Fallback to default scrollIntoView
              element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }

            // Focus on the first error field with delay
            setTimeout(() => {
              // Try to focus on the input element if it exists
              const inputElement = element.querySelector('input, select') as HTMLElement;
              if (inputElement?.focus) {
                inputElement.focus();
              } else if (element?.focus) {
                element.focus();
              }
            }, 400);
          }
        }
      }

      // Reset running state on validation error
      if (onRunningChange) {
        onRunningChange(false);
      } else {
        setInternalIsRunning(false);
      }
    }
  };

  // Render form field based on variable type
  const renderFormField = (variable: WorkflowVariable) => {
    if (!variable) {
      return null;
    }
    const { name, required, variableType } = variable;
    const value = variableValues[name];

    if (variableType === 'string') {
      const hasError = isFieldInvalid(name);
      return (
        <Form.Item
          key={name}
          label={<FormItemLabel name={name} required={required} />}
          name={name}
          rules={
            required
              ? [{ required: true, message: t('canvas.workflow.variables.inputPlaceholder') }]
              : []
          }
          data-field-name={name}
          className={cn({ 'has-validation-error': hasError })}
          validateStatus={hasError ? 'error' : undefined}
          help={
            hasError
              ? t('canvas.workflow.variables.thisFieldIsRequired') || 'This field is required'
              : undefined
          }
        >
          <Input.TextArea
            variant="filled"
            placeholder={t('canvas.workflow.variables.inputPlaceholder')}
            value={value}
            onChange={(e) => handleValueChange(name, e.target.value)}
            data-field-name={name}
            autoSize={{ minRows: 1, maxRows: 5 }}
            disabled={isFormDisabled}
            status={hasError ? 'error' : undefined}
          />
        </Form.Item>
      );
    }

    if (variableType === 'option') {
      const hasError = isFieldInvalid(name);
      const accountValues = (Array.isArray(value) ? value : variable.value || []).filter(
        (v) => v.account?.accountId,
      );

      return (
        <Form.Item
          key={name}
          label={<FormItemLabel name={name} required={required} />}
          name={name}
          className={cn({ 'has-validation-error': hasError })}
          validateStatus={hasError ? 'error' : undefined}
          help={
            hasError
              ? t('canvas.workflow.variables.thisFieldIsRequired') || 'This field is required'
              : undefined
          }
        >
          <div className="space-y-2">
            <div className="flex items-center gap-1 text-xs text-refly-text-3">
              <LuUsers size={12} />
              {t('canvas.workflow.variables.selectAccounts') || '选择账号'}
              <span className="ml-1">
                (
                {t('canvas.workflow.variables.selectedCount', {
                  count: accountValues.length,
                }) || `已选 ${accountValues.length} 个`}
                )
              </span>
            </div>
            {accountValues.length > 0 ? (
              <div className="flex flex-wrap gap-1 max-h-[200px] overflow-y-auto border border-refly-Card-Border rounded-lg p-2">
                {accountValues.map((v) => {
                  const acc = v.account!;
                  const platformName =
                    acc.platformType != null
                      ? PLATFORM_MAP[acc.platformType] || acc.platform
                      : acc.platform;
                  return (
                    <span
                      key={acc.accountId}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-refly-tertiary-hover"
                    >
                      {acc.userName || acc.accountId}
                      {platformName && <span className="text-refly-text-3">· {platformName}</span>}
                      <Tooltip title="移除账号">
                        <Button
                          type="text"
                          size="small"
                          icon={<LuX size={13} />}
                          className="!ml-0.5 !h-4 !w-4 !min-w-4 !p-0"
                          disabled={isFormDisabled}
                          onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            handleAccountRemove(variable, acc.accountId);
                          }}
                        />
                      </Tooltip>
                    </span>
                  );
                })}
              </div>
            ) : (
              <Button
                block
                onClick={() => setSelectionVariable(variable)}
                disabled={isFormDisabled}
              >
                {t('canvas.workflow.variables.selectAccounts') || '选择账号'}
              </Button>
            )}
            {accountValues.length > 0 && (
              <Button
                block
                onClick={() => setSelectionVariable(variable)}
                disabled={isFormDisabled}
              >
                {t('canvas.workflow.variables.selectAccounts') || '选择账号'}
              </Button>
            )}
          </div>
        </Form.Item>
      );
    }

    if (variableType === 'resource') {
      const hasError = isFieldInvalid(name);
      return (
        <Form.Item
          key={name}
          label={<FormItemLabel name={name} required={required} />}
          name={name}
          rules={
            required
              ? [{ required: true, message: t('canvas.workflow.variables.uploadPlaceholder') }]
              : []
          }
          className={cn({ 'has-validation-error': hasError })}
          validateStatus={hasError ? 'error' : undefined}
          help={
            hasError
              ? t('canvas.workflow.variables.thisFieldIsRequired') || 'This field is required'
              : undefined
          }
        >
          <div className="space-y-2" data-field-name={name}>
            {Array.isArray(value) && value.length > 0 && (
              <div className="flex flex-wrap gap-1 border border-refly-Card-Border rounded-lg p-2">
                {value.map((file: UploadFile) => (
                  <span
                    key={file.uid}
                    className="inline-flex px-2 py-0.5 rounded text-xs bg-refly-tertiary-hover"
                  >
                    {file.name}
                  </span>
                ))}
              </div>
            )}
            <Button block onClick={() => setSelectionVariable(variable)} disabled={isFormDisabled}>
              {t('canvas.workflow.variables.selectFiles') || '选择素材'}
            </Button>
          </div>
        </Form.Item>
      );
    }

    return null;
  };

  const workflowIsRunning = isRunning || isPolling;
  const selectedAccountValues = (selectionVariable?.value || [])
    .filter((value) => value.account?.accountId)
    .map((value) => value.account) as Account[];
  const selectedMediaFiles = (selectionVariable?.value || [])
    .filter((value) => value.resource?.fileId || value.resource?.assetId)
    .map((value) => ({
      fileId: value.resource.assetId || value.resource.fileId,
      assetId: value.resource.assetId,
      name: value.resource.name || value.resource.assetId || value.resource.fileId,
      type: value.resource.fileType || '',
      size: 0,
      storageKey: value.resource.storageKey,
    })) as MediaFile[];

  return (
    <>
      <Modal
        open={!!selectionVariable}
        title={
          selectionVariable?.variableType === 'option'
            ? t('canvas.workflow.variables.selectAccounts') || '选择账号'
            : t('canvas.workflow.variables.selectFiles') || '选择素材'
        }
        footer={null}
        onCancel={() => setSelectionVariable(null)}
        destroyOnHidden
        width={560}
      >
        {selectionVariable?.variableType === 'option' ? (
          <AccountTypeForm
            initialAccounts={selectedAccountValues}
            onAccountsSelect={handleAccountsSelect}
            isRequired={false}
          />
        ) : (
          <ResourceTypeForm
            fileList={[]}
            initialFiles={selectedMediaFiles}
            uploading={false}
            onFileUpload={async () => false}
            onFileRemove={() => undefined}
            onFilesSelect={handleLibraryFilesSelect}
            autoLoadLibrary
            isRequired={false}
          />
        )}
      </Modal>
      <div className={cn('w-full h-full gap-3 flex flex-col rounded-2xl', className)}>
        {
          <>
            {/* default show Form */}
            {
              <div className="p-3 sm:p-4 flex-1 overflow-y-auto">
                {/* Show loading state when loading */}
                {workflowVariables.length > 0 ? (
                  <>
                    <Form
                      form={form}
                      layout="horizontal"
                      className="space-y-3 sm:space-y-4"
                      initialValues={variableValues}
                    >
                      {workflowVariables.map((variable) => renderFormField(variable))}
                    </Form>

                    {/* Tools Dependency Form */}
                    {workflowApp?.canvasData && (
                      <div className="mt-5 ">
                        <ToolsDependencyChecker canvasData={workflowApp?.canvasData} />
                      </div>
                    )}
                  </>
                ) : loading ? null : (
                  <EmptyContent />
                )}
              </div>
            }

            <div className="p-3 border-t-[1px] border-x-0 border-b-0 border-solid border-refly-Card-Border bg-refly-bg-body-z0 rounded-b-lg flex flex-col gap-2">
              {creditUsage !== null && creditUsage !== undefined && (
                <div className="text-xs leading-4 text-refly-primary-default text-center">
                  {t('canvas.workflow.run.creditUsage', { count: creditUsage })}
                </div>
              )}
              <Tooltip
                title={
                  !workflowIsRunning && !hasFirstSuccessExecutionToday
                    ? t('canvas.workflow.run.runTooltip')
                    : undefined
                }
                placement="top"
                arrow={false}
                styles={{
                  root: { maxWidth: 300 },
                }}
              >
                <Button
                  className={cn(
                    'w-full h-8 text-sm group',
                    !workflowIsRunning
                      ? 'bg-refly-text-0 text-refly-bg-body-z0 hover:!bg-refly-text-0 hover:!text-refly-bg-body-z0 hover:opacity-80'
                      : '',
                  )}
                  {...(workflowIsRunning ? { color: 'primary' } : { type: 'default' })}
                  icon={
                    workflowIsRunning ? (
                      <StopCircle size={16} className="translate-y-[1px]" />
                    ) : null
                  }
                  onClick={workflowIsRunning ? handleAbort : handleRun}
                  loading={loading}
                  disabled={loading || (workflowIsRunning && !executionId)}
                >
                  {workflowIsRunning
                    ? t('canvas.workflow.run.abort.abortButton') || 'Abort'
                    : t('canvas.workflow.run.run') || 'Run'}

                  {!workflowIsRunning && !hasFirstSuccessExecutionToday && planType === 'free' && (
                    <img
                      src={GiftIcon}
                      alt="gift"
                      className="w-[18px] h-[18px] group-hover:animate-shake"
                    />
                  )}
                </Button>
              </Tooltip>
            </div>
          </>
        }
      </div>
    </>
  );
};
